/* istanbul ignore file */
import { mediaQuery } from '@noths/polaris-client-styles';
import { css } from 'styled-components';

import { minBreakpoints } from 'client-ribbons-base/constants/breakpoints';

export const GRID_COLUMN_GAP_SMALL = 12;
export const GRID_COLUMN_GAP_MEDIUM = 20;
export const GRID_COLUMN_GAP_LARGE = 20;
export const GRID_COLUMN_GAP_XLARGE = 24;
const DEFAULT_GRID_ROW_GAP_SMALL = 32;
const DEFAULT_GRID_ROW_GAP_LARGE = 64;
const GUTTER_SMALL = '16px';
const GUTTER_MEDIUM = '16px';
const GUTTER_LARGE = '24px';
const GUTTER_XLARGE = '24px';

const MAXIMUM_CONTENT_WIDTH = '1440px';
const MAX_CONTENT_WIDTH_AVOIDING_FOCUS_CROP = '1450px';
const PERSISTENT_UI_WIDTH = '192px';

export const GRID_NUMBER_OF_COLUMNS = 12;

interface CreateGridOptions {
  columnGapLarge?: number;
  columnGapMedium?: number;
  columnGapSmall?: number;
  columnGapXLarge?: number;
  numberOfColumns: number;
  rowGapLarge?: number;
  rowGapMedium?: number;
  rowGapSmall?: number;
  rowGapXLarge?: number;
}

export const createGrid = ({
  columnGapLarge = GRID_COLUMN_GAP_LARGE,
  columnGapMedium = GRID_COLUMN_GAP_MEDIUM,
  columnGapSmall = GRID_COLUMN_GAP_SMALL,
  columnGapXLarge = GRID_COLUMN_GAP_XLARGE,
  numberOfColumns,
  rowGapLarge = DEFAULT_GRID_ROW_GAP_LARGE,
  rowGapMedium = DEFAULT_GRID_ROW_GAP_SMALL,
  rowGapSmall = DEFAULT_GRID_ROW_GAP_SMALL,
  rowGapXLarge = DEFAULT_GRID_ROW_GAP_LARGE,
}: CreateGridOptions) => css`
  display: grid;
  grid-column-gap: ${columnGapSmall}px;
  grid-row-gap: ${rowGapSmall}px;
  grid-template-columns: repeat(${numberOfColumns}, 1fr);

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      grid-column-gap: ${columnGapMedium}px;
      grid-row-gap: ${rowGapMedium}px;
    `,
  )};

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      grid-column-gap: ${columnGapLarge}px;
      grid-row-gap: ${rowGapLarge}px;
    `,
  )};

  ${mediaQuery(
    { min: minBreakpoints.XL },
    css`
      grid-column-gap: ${columnGapXLarge}px;
      grid-row-gap: ${rowGapXLarge}px;
    `,
  )};
`;

export const grid = createGrid({ numberOfColumns: GRID_NUMBER_OF_COLUMNS });

export const containerWithGutters = css`
  padding-left: ${GUTTER_SMALL};
  padding-right: ${GUTTER_SMALL};
  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      padding-left: ${GUTTER_MEDIUM};
      padding-right: ${GUTTER_MEDIUM};
    `,
  )};

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      padding-left: ${GUTTER_LARGE};
      padding-right: ${GUTTER_LARGE};
    `,
  )};

  ${mediaQuery(
    { min: minBreakpoints.XL },
    css`
      padding-left: ${GUTTER_XLARGE};
      padding-right: ${GUTTER_XLARGE};
    `,
  )};
`;

export const maxWidthContainerWithoutGutters = css`
  margin: 0 auto;
  ${mediaQuery(
    { min: minBreakpoints.XL },
    css`
      max-width: ${MAXIMUM_CONTENT_WIDTH};
    `,
  )};
`;

const extraMaxWidthContainerWithoutGutters = css`
  margin: 0 auto;
  ${mediaQuery(
    { min: minBreakpoints.XL },
    css`
      max-width: ${MAX_CONTENT_WIDTH_AVOIDING_FOCUS_CROP};
    `,
  )};
`;

export const maxWidthContainer = css`
  ${maxWidthContainerWithoutGutters}
  ${containerWithGutters};
`;

export const extraMaxWidthContainer = css`
  ${extraMaxWidthContainerWithoutGutters}
  ${containerWithGutters};
`;

export const persistentUIContainer = css`
  margin: 0 auto;
  padding-left: ${GUTTER_SMALL};
  padding-right: ${GUTTER_SMALL};

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      padding-left: ${GUTTER_MEDIUM};
      padding-right: ${GUTTER_MEDIUM};
    `,
  )};

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      display: grid;
      grid-column-gap: ${GUTTER_LARGE};
      grid-template-columns: ${PERSISTENT_UI_WIDTH} 1fr;
      padding-left: ${GUTTER_LARGE};
      padding-right: ${GUTTER_LARGE};
    `,
  )};

  ${mediaQuery(
    { min: minBreakpoints.XL },
    css`
      padding-left: ${GUTTER_XLARGE};
      padding-right: ${GUTTER_XLARGE};
      max-width: ${MAXIMUM_CONTENT_WIDTH};
    `,
  )};
`;
